/**
 * O botão do Lumma Store — um só, para o sistema inteiro.
 *
 * Este arquivo existe porque o `Botao` anterior era, na prática, o botão das
 * telas de ACESSO: 52px de altura e raio 10. A spec 01 define o botão do
 * sistema como 34px e raio 8, então nenhuma tela interna conseguia usá-lo — e
 * todas reescreveram o seu. O resultado eram 28 botões primários à mão, com
 * cinco alturas diferentes (34, 36, 38, 40 e 44), METADE sem estado de hover, e
 * três tratamentos distintos de desabilitado.
 *
 * A correção não é "usar o componente com mais disciplina": é fazer o
 * componente cobrir os portes que a spec pede, para que fugir dele passe a ser
 * mais trabalhoso do que usá-lo.
 *
 * Os cinco estados da spec 01 estão todos aqui, e nenhum é opcional:
 *
 *   padrão        a variante
 *   hover         `hover:` em toda variante, inclusive na secundária
 *   foco          o `:focus-visible` global de tokens.css (contorno + offset)
 *   erro/perigo   a variante `perigo`, para a ação destrutiva
 *   desabilitado  `disabled:` uniforme, com `cursor:not-allowed` vindo da base
 *
 * Foco NÃO é tratado aqui de propósito. `tokens.css` já dá contorno visível a
 * tudo que recebe foco; um `focus:ring` local por cima só empilharia dois
 * indicadores — que foi exatamente o bug que a base resolveu.
 */
import type { ButtonHTMLAttributes, ReactNode } from 'react'
import { Link } from 'react-router-dom'

import { Icone, type NomeIcone } from './Icone'
import { classesDeBotao, type PorteBotao, type VarianteBotao } from './botao-estilo'

// Reexportado como TIPO: `export type` some na compilação e não conta como
// exportação de não-componente para o react-refresh.
export type { PorteBotao, VarianteBotao }

type PropsBotao = Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'className'> & {
  variante?: VarianteBotao
  porte?: PorteBotao
  larguraTotal?: boolean
  /** Ícone à esquerda do rótulo. */
  icone?: NomeIcone
  /**
   * Sobrepõe a altura do porte. Use só quando o handoff pedir um valor que não
   * está na tabela — hoje são dois: 54px no login e 50px no wizard de cadastro.
   */
  altura?: number
  children?: ReactNode
}

export function Botao({
  variante = 'primario',
  porte = 'padrao',
  larguraTotal = false,
  icone,
  altura,
  type = 'button',
  children,
  ...resto
}: PropsBotao) {
  return (
    <button
      {...resto}
      // `type` explícito e com padrão `button`: o padrão do HTML é `submit`, e
      // um botão de "Cancelar" dentro de um `<form>` que envia o formulário em
      // vez de fechá-lo é um bug que só aparece depois de publicado.
      type={type}
      style={altura ? { height: altura } : undefined}
      className={classesDeBotao({ variante, porte, larguraTotal })}
    >
      {icone && <Icone nome={icone} tamanho={porte === 'padrao' ? 18 : 19} />}
      {children}
    </button>
  )
}

/**
 * Um `<Link>` com aparência de botão — "Novo produto", "Voltar para a lista".
 *
 * É um LINK de verdade, e não um botão que chama `navigate()`: quem abre em
 * nova aba com o meio do mouse, quem copia o endereço, quem usa o leitor de
 * tela e pede a lista de links — todos dependem de ser um `<a>` com `href`. Um
 * `<button>` pintado de link tira as três coisas.
 */
export function LinkBotao({
  para,
  variante = 'primario',
  porte = 'padrao',
  larguraTotal = false,
  icone,
  children,
}: {
  para: string
  variante?: VarianteBotao
  porte?: PorteBotao
  larguraTotal?: boolean
  icone?: NomeIcone
  children: ReactNode
}) {
  return (
    <Link to={para} className={classesDeBotao({ variante, porte, larguraTotal })}>
      {icone && <Icone nome={icone} tamanho={porte === 'padrao' ? 18 : 19} />}
      {children}
    </Link>
  )
}

/**
 * Botão só de ícone — o "⋮" da linha, o X que fecha, o lápis da célula.
 *
 * Separado do `Botao` porque a exigência dele é outra: ele é QUADRADO e
 * obrigatoriamente carrega um nome acessível, já que não tem rótulo visível
 * para o leitor de tela ler. Deixar isso como prop opcional do `Botao` é como
 * metade dos ícones acaba anunciada como "botão".
 */
export function BotaoIcone({
  icone,
  rotulo,
  tom = 'neutro',
  tamanho = 32,
  ...resto
}: Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'className' | 'children'> & {
  icone: NomeIcone
  /** Lido pelo leitor de tela. Diga a AÇÃO: "Ações de Ana", não "menu". */
  rotulo: string
  tom?: 'neutro' | 'suave' | 'perigo'
  tamanho?: number
}) {
  const cores = {
    neutro: 'text-texto-suave hover:bg-divisoria hover:text-tinta',
    suave: 'bg-divisoria text-texto hover:bg-borda',
    perigo: 'text-erro-texto hover:bg-erro-suave',
  }[tom]

  return (
    <button
      {...resto}
      type={resto.type ?? 'button'}
      style={{ width: tamanho, height: tamanho }}
      className={`flex shrink-0 items-center justify-center rounded-sistema transition-colors duration-150 disabled:text-inativo disabled:hover:bg-transparent ${cores}`}
    >
      <Icone nome={icone} tamanho={Math.round(tamanho * 0.58)} />
      <span className="sr-only">{rotulo}</span>
    </button>
  )
}
